import { useMemo } from 'react';
import Fuse from 'fuse.js';
import {
  useBdaasAirports,
  useOneCmsLocaleAirports,
} from './airportsFetchyeHooks';
import { useOneCmsLocaleMinimumSearchLength } from '../oneCmsIntlHooks/oneCmsIntlHelperHooks';

export const useBdaasAirportsMappedByIata = () => {
  const { data: bdaasAirportsResponse } = useBdaasAirports();

  return useMemo(() => {
    const bdaasAirports = Array.isArray(bdaasAirportsResponse?.body)
      ? bdaasAirportsResponse.body
      : [];
    return bdaasAirports.reduce((acc, airport) => {
      acc[airport.iata] = airport;
      return acc;
    }, {});
  }, [bdaasAirportsResponse]);
};

export const useIndexedAirports = () => {
  const { data: bdaasAirportsResponse } = useBdaasAirports();

  return useMemo(() => {
    const bdaasAirports = Array.isArray(bdaasAirportsResponse?.body)
      ? bdaasAirportsResponse.body
      : [];

    // options for the fuse search
    const options = {
      keys: [
        'iata',
        'name',
        'location.city',
        'location.country_name',
        'location.description',
      ],
      includeScore: true,
      threshold: 0.3, // adjust this to make the search more or less strict
      minMatchCharLength: 2,
    };

    return new Fuse(bdaasAirports, options);
  }, [bdaasAirportsResponse]);
};

export const useTranslatedAiportResults = (query = '') => {
  const bdaasAirportsByIata = useBdaasAirportsMappedByIata();
  const indexedAirports = useIndexedAirports();
  const { data: oneCmsAirportsResponse } = useOneCmsLocaleAirports();

  const minQueryLength = useOneCmsLocaleMinimumSearchLength();

  return useMemo(() => {
    if (query.length < minQueryLength) {
      return [];
    }

    const matchingAirportsByIata = {};

    const lowerCaseQuery = query.toLowerCase();
    const oneCmsLocaleAirportsByIata = oneCmsAirportsResponse?.body ?? {};
    if (oneCmsLocaleAirportsByIata) {
      Object.entries(oneCmsLocaleAirportsByIata).forEach(
        ([iata, localeAirport]) => {
          if (
            iata?.toLowerCase()?.includes(lowerCaseQuery) ||
            localeAirport?.name?.toLowerCase()?.includes(lowerCaseQuery) ||
            localeAirport?.city?.toLowerCase()?.includes(lowerCaseQuery) ||
            localeAirport?.description?.toLowerCase()?.includes(lowerCaseQuery)
          ) {
            const bdaasAirport = bdaasAirportsByIata[iata] ?? {};
            matchingAirportsByIata[iata] = {
              ...bdaasAirport,
              iata,
              name: localeAirport?.name,
              location: {
                ...bdaasAirport?.location,
                description: localeAirport?.description,
              },
            };
          }
        }
      );
    }

    const results = indexedAirports.search(query);
    return results.map((result) => result.item);
  }, [
    query,
    minQueryLength,
    oneCmsAirportsResponse?.body,
    indexedAirports,
    bdaasAirportsByIata,
  ]);
};
